import { Check, Copy, Eye, RefreshCw, ShoppingCart } from 'lucide-react'
import { FormProvider } from 'react-hook-form'

import { useOrdersTable } from '@/hooks/features/orders/useOrdersTable'
import { useDataTable } from '@/hooks/ui'

import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { ColumnVisibilityDropdown, DataTable, NoData } from '@/components/ui/data-table'
import { Pagination } from '@/components/ui/pagination-controls'

import { PageHeader, StatsCards, StatusBadge } from '@/components/atoms'
import { ORDER_STATUS_OPTIONS } from '@/constants/statuses'

import { OrderModel } from '@/types/model/Order'
import { OrdersFilters } from './OrdersFilters'
import { OrderNoteSelect } from './cells/OrderNoteSelect'
import { OrderSourceSelect } from './cells/OrderSourceSelect'
import { useBusiness } from '@/contexts/BusinessContext'
import { copyText } from '@/utils'
import { useToast } from '@/hooks'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui'
import { useFetchOrderNotes } from '@/hooks/features/order-note'
import { useFetchOrderSources } from '@/hooks/features/order-source'

type TableColumns =
    | 'order_ref'
    | 'created_at'
    | 'customer_name'
    | 'total'
    | 'order_note'
    | 'order_source'
    | 'payment_status'
    | 'status'
    | 'actions'

/** Display names for the stored payment method codes. */
const PAYMENT_METHOD_LABELS: Record<string, string> = {
    cod: 'COD',
    ssl_commerz: 'SSLCommerz',
    bkash: 'bKash',
    nagad: 'Nagad',
}

const DEFAULT_VISIBLE_COLUMNS: TableColumns[] = [
    'order_ref',
    'created_at',
    'customer_name',
    'total',
    'order_note',
    'order_source',
    'payment_status',
    'status',
    'actions',
]

// Same window as the server's POS_LATE_SYNC_MINUTES.
const POS_LATE_SYNC_MS = 5 * 60_000

const POS_FILTERS: { label: string; value?: string }[] = [
    { label: 'All' },
    { label: 'Credit', value: 'credit' },
    { label: 'Returned', value: 'returned' },
    { label: 'From offline', value: 'offline' },
]

/** `channel="pos"`: Point of sale → Orders, the till list with the channel fixed (online counts and Create left out). */
export const OrdersTable = ({ channel }: { channel?: 'pos' } = {}) => {
    const pos = channel === 'pos'
    const [copied, setCopied] = useState({
        orderRef: '',
    })
    const {
        loading,
        data,
        pagination,
        setPage,
        setPageSize,
        handleRowClick,
        handleCreateOrder,
        formMethods,
        refresh,
        groups,
    } = useOrdersTable(channel)

    // Header quick-find lands here as /orders?search=…
    const [searchParams] = useSearchParams()
    const urlSearch = searchParams.get('search')
    useEffect(() => {
        if (urlSearch === null) return
        formMethods.setValue('filtersForm.search' as never, urlSearch as never)
        formMethods.setValue('filters.search', urlSearch)
        formMethods.setValue('pagination.currentPage', 1)
    }, [urlSearch, formMethods])

    const { data: notes } = useFetchOrderNotes({
        shouldFetchList: true,
    })
    const { data: sources } = useFetchOrderSources({
        shouldFetchList: true,
    })

    const { formatCurrency } = useBusiness();
    const { toast } = useToast()
    const handleCopy = async (orderRef: string) => {
        try {
            await copyText(orderRef)
            setCopied({ orderRef })
            toast({
                title: 'Order reference copied',
                description: `Order ${orderRef} copied to clipboard.`,
            })
        } catch {
            toast({
                title: 'Error',
                description: 'Could not copy reference to clipboard.',
                variant: 'destructive',
            })
        }
    }


    const { columns, changeColumnVisibility, disabledColumns } = useDataTable<
        OrderModel.Order,
        TableColumns
    >({
        columns: [
            {
                accessorKey: 'order_ref',
                columnLabel: () => 'Order',
                cell: (data) => (
                    <div className="flex flex-col gap-0.5">
                        <span
                            className="flex w-fit cursor-pointer items-center gap-1 font-semibold text-ink"
                            title="Copy order reference"
                            onClick={() => handleCopy(data?.order_ref)}>
                            {data?.order_ref}
                            {copied?.orderRef === data?.order_ref ? (
                                <Check className="size-3.5 text-success-dark" />
                            ) : (
                                <Copy className="size-3.5 text-muted-foreground" />
                            )}
                        </span>
                        <span className="text-xs text-muted-foreground">
                            Placed <span className="tabular-nums">{new Date(data.created_at).toLocaleString()}</span>
                        </span>
                        <span className="text-xs text-muted-foreground">
                            Updated <span className="tabular-nums">{new Date(data.updated_at).toLocaleString()}</span>
                        </span>
                        {data.terminal && (
                            <span className="text-xs text-muted-foreground">
                                {[data.terminal.branch?.name, data.terminal.name].filter(Boolean).join(' · ')}
                            </span>
                        )}
                        {data.staff?.name && <span className="text-xs text-muted-foreground">Cashier {data.staff.name}</span>}
                        {data.synced_at && new Date(data.synced_at).getTime() - new Date(data.created_at).getTime() >= POS_LATE_SYNC_MS && (
                            <span className="text-xs text-warning-dark">
                                Rung up offline · synced <span className="tabular-nums">{new Date(data.synced_at).toLocaleString()}</span>
                            </span>
                        )}
                    </div>
                ),
                hasSorting: true,
            },
            {
                accessorKey: 'customer_name',
                columnLabel: () => 'Customer',
                cell: (data) => (
                    <div className="flex flex-col gap-0.5">
                        <span className="font-medium text-ink">{data.customer_info?.name || 'Guest'}</span>
                        <span className="text-xs tabular-nums text-muted-foreground">{data.customer_info?.phone}</span>
                    </div>
                ),
            },
            {
                accessorKey: 'total',
                columnLabel: () => 'Total',
                cell: (data) => (
                    <div className="flex flex-col gap-0.5 tabular-nums md:items-end">
                        <span className="font-semibold text-ink">{formatCurrency(data.total)}</span>
                        <span className="text-xs text-muted-foreground">Subtotal {formatCurrency(data.subtotal)}</span>
                        <span className="text-xs text-muted-foreground">Delivery {formatCurrency(data.delivery_charge)}</span>
                        <span className="text-xs text-muted-foreground">Discount {formatCurrency(data.discount)}</span>
                    </div>
                ),
                classes: () => ({
                    column: 'text-right',
                    cell: 'text-right',
                }),
            },
            {
                accessorKey: 'order_note',
                columnLabel: () => 'Note & source',
                cell: (data) => {
                    const expandedNote = Array.isArray(data.order_note_id) ? data.order_note_id[0] : null
                    const noteId = typeof data.order_note_id === 'string' ? data.order_note_id : expandedNote?._id || null
                    const expandedSource = Array.isArray(data.order_source_id) ? data.order_source_id[0] : null
                    const sourceId = typeof data.order_source_id === 'string' ? data.order_source_id : expandedSource?._id || null
                    return (
                        <div onClick={(e) => e.stopPropagation()} className="flex flex-col gap-1">
                            <OrderNoteSelect
                                orderId={data._id}
                                currentNoteId={noteId}
                                notes={notes}
                                onUpdate={refresh}
                            />
                            <OrderSourceSelect
                                orderId={data._id}
                                currentSourceId={sourceId}
                                sources={sources}
                                onUpdate={refresh}
                            />
                        </div>
                    )
                },
            },

            {
                accessorKey: 'payment_status',
                columnLabel: () => 'Payment',
                cell: (data) => (
                    <div className="flex flex-col items-start gap-1">
                        <StatusBadge type="payment" status={data.payment_status} />
                        <span className="text-xs text-muted-foreground">
                            {PAYMENT_METHOD_LABELS[data.payment_method] ?? data.payment_method}
                        </span>
                    </div>
                ),
            },
            {
                accessorKey: 'status',
                columnLabel: () => 'Status',
                cell: (data) => <StatusBadge type="order" status={data.status} />,
            },
            {
                accessorKey: 'actions',
                columnLabel: () => 'Actions',
                cell: (data) => (
                    <div className="flex justify-end">
                        <Button
                            variant="outline"
                            size="icon"
                            aria-label={`View order ${data.order_ref}`}
                            onClick={() => handleRowClick(data._id)}>
                            <Eye className="size-4" />
                        </Button>
                    </div>
                ),
                classes: () => ({
                    column: 'text-right',
                    cell: 'text-right',
                }),
                isAction: true,
            },
        ],
        visibleColumns: DEFAULT_VISIBLE_COLUMNS,
        disabledColumns: ['actions'],
    })

    return (
        <div className="space-y-4">
            {pos ? (
                <PageHeader title="POS orders" description="Sales rung up on your tills, every branch" />
            ) : (
                <PageHeader
                    title="Orders"
                    description="Manage and fulfill customer orders"
                    onAddClick={handleCreateOrder}
                    addPermission="orders:place"
                    addButtonText="Create order"
                />
            )}

            <Card className="max-sm:border-0 max-sm:bg-transparent max-sm:shadow-none">
                <CardHeader className="gap-3 max-sm:px-0">
                    {pos && (
                        <div className="flex flex-wrap gap-2">
                            {POS_FILTERS.map((f) => (
                                <Button
                                    key={f.label}
                                    size="sm"
                                    variant={(formMethods.watch('filters.pos_filter') ?? undefined) === f.value ? 'default' : 'outline'}
                                    onClick={() => {
                                        formMethods.setValue('filters.pos_filter', f.value)
                                        formMethods.setValue('pagination.currentPage', 1)
                                    }}>
                                    {f.label}
                                </Button>
                            ))}
                        </div>
                    )}
                    {!pos && <StatsCards
                        variant="tag"
                        stats={[
                            {
                                title: 'All',
                                value: groups?.total_count ?? 0,
                                icon: ShoppingCart,
                                active: !formMethods.watch('filters.status'),
                                onClick: () => {
                                    formMethods.setValue('filters.status', null)
                                    formMethods.setValue('pagination.currentPage', 1)
                                },
                            },
                            ...ORDER_STATUS_OPTIONS.map(({ label, value }) => ({
                                title: label,
                                value: groups?.[`${value}_count` as keyof OrderModel.OrderGroups] ?? 0,
                                active: formMethods.watch('filters.status') === value,
                                onClick: () => {
                                    formMethods.setValue('filters.status', value)
                                    formMethods.setValue('pagination.currentPage', 1)
                                },
                            })),
                        ]}
                    />}
                    <div className="flex flex-col justify-between gap-2 lg:flex-row lg:items-center">
                        <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
                            <Pagination.PageSizeDropdown
                                pageSize={pagination.pageSize}
                                onPageSizeChange={setPageSize}
                            />
                            <FormProvider {...formMethods}>
                                <OrdersFilters fixedChannel={channel} />
                            </FormProvider>
                        </div>

                        <div className="order-first flex items-center justify-end gap-2 lg:order-last">
                            <Button
                                variant="outline"
                                size="icon"
                                aria-label="Refresh orders"
                                onClick={refresh}
                                disabled={loading}>
                                <RefreshCw className={`size-4 ${loading ? 'animate-spin' : ''}`} />
                            </Button>
                            <ColumnVisibilityDropdown
                                columns={columns}
                                onColumnVisibilityChange={changeColumnVisibility}
                                disabledColumns={disabledColumns}
                            />
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    {!loading && data.length === 0 ? (
                        <div className="border-t border-border-subtle p-8 max-sm:border-0">
                            {pos ? (
                                <NoData title="No POS orders found" />
                            ) : (
                                <NoData
                                    title="No orders found"
                                    btnText="Create order"
                                    openCreateDialog={handleCreateOrder}
                                    createPermission="orders:place"
                                />
                            )}
                        </div>
                    ) : (
                        <>
                            <div className="md:border-t md:border-border-subtle">
                                <DataTable
                                    columns={columns}
                                    data={data}
                                    loading={loading}
                                />
                            </div>
                            <div className="px-0 pb-0 sm:px-4 sm:pb-4">
                                <Pagination.Buttons
                                    currentPage={pagination.currentPage}
                                    totalItems={pagination.totalCount}
                                    pageSize={pagination.pageSize}
                                    onPageChange={setPage}
                                />
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    )
}
